import clone from "clone";
import { useMemo } from "react";
import type { ImmutableObject } from "@/lib/immutable";
import { createStore, type Store, useStore } from "./store";

interface IPaletteColor {
	main: string;
}

interface IPaletteGrey {
	[key: number]: string;
}

interface IPalette {
	mode: "light" | "dark";
	primary: IPaletteColor;
	secondary: IPaletteColor;
	grey: IPaletteGrey;
	divider: string;
	text: {
		primary: string;
		secondary: string;
	};
	background: {
		default: string;
		paper: string;
	};
	action: {
		active: string;
		hover: string;
		selected: string;
		disabled: string;
		disabledBackground: string;
		focus: string;
	};
	siteBackground: string;
	siteHeaderBackground: string;
	siteFooterBackground: string;
	siteTextPrimary: string;
	siteTextSecondary: string;
	siteAccent: string;
	siteAccentContrastText: string;
	siteCardBackground: string;
	siteCardBorder: string;
	siteCardBackgroundLighter1: string;
	siteSideBar: string;
	siteSideBarText: string;
	siteSideBarText2: string;
	siteSideBarContrastText: string;
}
export interface ITheme {
	palette: IPalette;
}

const basePalette = {
	primary: { main: "var(--primary)" },
	secondary: { main: "var(--destructive)" },
	grey: {
		50: "oklch(0.985 0.001 106.423)",
		100: "oklch(0.97 0.001 106.424)",
		200: "oklch(0.923 0.003 48.717)",
		300: "oklch(0.87 0.006 56.043)",
		400: "oklch(0.709 0.01 56.259)",
		500: "oklch(0.553 0.013 58.071)",
		600: "oklch(0.45 0.012 58.071)",
		700: "oklch(0.35 0.01 56.259)",
		800: "oklch(0.268 0.007 34.298)",
		900: "oklch(0.216 0.006 56.043)",
	},
	divider: "var(--border)",
	text: {
		primary: "var(--foreground)",
		secondary: "var(--muted-foreground)",
	},
	background: {
		default: "var(--background)",
		paper: "var(--card)",
	},
	action: {
		active: "var(--foreground)",
		hover: "color-mix(in oklch, var(--foreground) 8%, transparent)",
		selected: "color-mix(in oklch, var(--foreground) 12%, transparent)",
		disabled: "var(--muted-foreground)",
		disabledBackground: "var(--muted)",
		focus: "color-mix(in oklch, var(--foreground) 12%, transparent)",
	},
	siteBackground: "var(--background)",
	siteHeaderBackground: "var(--background)",
	siteFooterBackground: "var(--background)",
	siteTextPrimary: "var(--foreground)",
	siteTextSecondary: "var(--muted-foreground)",
	siteAccent: "var(--primary)",
	siteAccentContrastText: "var(--primary-foreground)",
	siteCardBackground: "var(--card)",
	siteCardBorder: "1px solid var(--border)",
	siteCardBackgroundLighter1: "var(--muted)",
	siteSideBar: "var(--sidebar)",
	siteSideBarText: "var(--sidebar-foreground)",
	siteSideBarText2: "var(--muted-foreground)",
	siteSideBarContrastText: "var(--sidebar-primary)",
};

export const darkTheme: ITheme = {
	palette: {
		mode: "dark",
		...basePalette,
	},
};

export const lightTheme: ITheme = {
	palette: {
		mode: "light",
		...basePalette,
	},
};

export class ThemeModel {
	themeName: string;
	theme: ITheme;

	constructor(themeName: string, theme: ITheme) {
		this.themeName = themeName;
		this.theme = theme;
	}
}

// global for theme state
const storefront_theme_key = "storefront-theme";
const themeName = window.localStorage.getItem(storefront_theme_key);
let initialTheme: ThemeModel | undefined;
if (themeName) {
	try {
		if (themeName === "dark" || themeName === "light") {
			initialTheme = {
				themeName: themeName,
				theme: themeName === "light" ? clone(lightTheme) : clone(darkTheme),
			};
		}
	} catch (error) {
		console.log(error);
	}
}
if (!initialTheme) {
	const defaultDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
	const themeName = defaultDark ? "dark" : "light";
	window.localStorage.setItem(storefront_theme_key, themeName);
	const theme = defaultDark ? clone(darkTheme) : clone(lightTheme);
	initialTheme = {
		themeName: themeName,
		theme: theme,
	};
}
const themeStateStore = createStore<ThemeModel>(initialTheme);

export class ThemeState {
	private themeState: Store<ThemeModel>;

	constructor(themeState: Store<ThemeModel>) {
		this.themeState = themeState;
	}

	get(): ImmutableObject<ThemeModel> {
		return this.themeState.getState();
	}

	getTheme(): ITheme {
		return clone(this.themeState.getState().theme) as ITheme;
	}

	set(themeName: string) {
		let theme = clone(darkTheme);
		if (themeName === "light") {
			theme = lightTheme;
		}
		this.themeState.setState((prev) => ({
			...prev,
			themeName: clone(themeName),
			theme: theme,
		}));
		this.saveToLocalStorage();
	}

	isDark() {
		return this.themeState.getState().themeName === "dark";
	}

	private saveToLocalStorage() {
		window.localStorage.setItem(
			storefront_theme_key,
			this.themeState.getState().themeName,
		);
	}
}

export function useThemeState(): ThemeState {
	useStore(themeStateStore);
	return useMemo(() => new ThemeState(themeStateStore), []);
}
